import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationCancel, NavigationCancellationCode, Router } from '@angular/router';
import { firstValueFrom, forkJoin, tap, timeout, type Observable } from 'rxjs';
import type {
  AuthUserSummary,
  ChangePasswordRequest,
  EffectivePermissions,
  FindPasswordRequest,
  FindPasswordResponse,
  LoginRequest,
  LoginResponse,
  MenuItem,
  MeResponse,
  PermissionAction,
  RefreshResponse,
} from '@paytms/shared';
import { environment } from '../../../environments/environment';
import { isAdminRole } from '../../shared/codes/role';

const STORAGE_ACCESS = 'paytms.accessToken';
const STORAGE_REFRESH = 'paytms.refreshToken';
/**
 * 세션 절대 만료 시각 — 토큰과 같은 방식으로 저장한다. 새로고침 직후 `/auth/me` 응답이
 * 오기 전 짧은 구간에도 헤더 카운트다운이 비지 않게 하는 것이 목적이다.
 */
const STORAGE_SESSION_EXPIRES = 'paytms.sessionExpiresAt';
/**
 * 세션 유지시간(초). 만료 시각과 같이 저장하는 이유도 같다 — 새로고침 직후 `/auth/me`가
 * 오기 전에 사용자가 뭔가 누르면 그 시점부터 잔여시간을 다시 그려야 한다.
 */
const STORAGE_SESSION_TTL = 'paytms.sessionTtlSeconds';
/** 최초 로그인 시각 — 새로고침해도 헤더 접속시각이 그대로여야 한다 */
const STORAGE_SESSION_STARTED = 'paytms.sessionStartedAt';

/**
 * revoke 응답을 기다리는 한도. 넘기면 서버 행은 절대 만료에 맡기고 로컬 세션부터 정리한다 —
 * 화면만 로그인 페이지인 채 살아 있는 세션이 남는 쪽이 더 위험하다 (공용 PC).
 */
const REVOKE_TIMEOUT_MS = 3000;

/**
 * 로그인 화면에 붙는 이탈 사유 — 사유 없이 튕기면 사용자는 자기가 뭘 잘못했는지 모른다.
 * `expired`: 접속 시간 만료 / `signed-out`: 다른 탭에서 로그아웃
 */
export type LogoutReason = 'expired' | 'signed-out';

/** 저장·응답의 ISO 문자열 → Date. 값이 없거나 깨졌으면 null(=카운트다운 미표시) */
function parseExpiresAt(value: string | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** 저장·응답의 TTL(초) → 밀리초. 값이 없거나 0 이하·비정상이면 null(=TTL 모름) */
function parseTtlMs(value: number | string | null | undefined): number | null {
  const seconds = typeof value === 'string' ? Number(value) : value;
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) {
    return null;
  }
  return seconds * 1000;
}

/**
 * Signal 기반 인증 상태 저장소 (규장각 fc4df17c 패턴).
 * - 토큰은 메모리(signal) + localStorage 이중 보관 — 인터셉터가 동기 접근, 새로고침 유지
 * - 사용자·메뉴는 저장하지 않고 부팅 시 /auth/me + /menus 재검증으로 복원
 *   (만료 토큰으로 로그인 상태를 복원하는 87eee66b 안티패턴 방지)
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly apiBase = environment.apiBaseUrl;

  private readonly accessTokenSignal = signal<string | null>(null);
  private readonly refreshTokenSignal = signal<string | null>(null);

  readonly user = signal<AuthUserSummary | null>(null);
  readonly menus = signal<MenuItem[]>([]);
  /**
   * 유효 권한 (계층 기본 권한 − 사용자별 차단). **화면 버튼 제어의 유일한 창구**다.
   * 계층(orgType)으로 직접 판단하면 사용자별 차단이 반영되지 않아 버튼은 활성인 채
   * 서버가 403을 낸다. 데이터 범위(누구의 데이터를 보는가)는 별개 축이라 orgType이 남는다.
   */
  readonly permissions = signal<EffectivePermissions>([]);
  /**
   * **최초 로그인 시각** — GNB 괄호 안 접속시각 표기용 (대표 확정 2026-09-09: 접속시간은
   * 그대로 두고 잔여시간만 재세팅). 로그인에서 정해지고 새로고침 복원에서는 저장값을 되살린다
   * — 복원할 때마다 지금 시각을 넣으면 F5 한 번에 접속시각이 방금으로 뛴다.
   */
  readonly sessionStartedAt = signal<Date | null>(null);
  /**
   * 세션 만료 시각 — **유휴 기준**이다 (클라이언트 대표 확정 2026-09-09:
   * "조작할 때마다 120분이 재세팅"). 조작이 있으면 뒤로 밀리고, 조작이 없는 동안만 줄어든다.
   * GNB 잔여시간 카운트다운과 자동 로그아웃의 유일한 기준값이다.
   * null이면(TTL·만료 시각을 모르는 옛 토큰) 카운트다운을 표시하지 않는다.
   *
   * 미는 주체는 두 갈래다 — 서버 응답(`setSessionExpiry`)과, 조작 직후의 낙관적 반영
   * (`pushSessionExpiryFromActivity`). 후자가 없으면 스로틀 간격(`ACTIVITY_RENEW_THROTTLE_MS`)
   * 만큼 화면이 뒤처져 "눌렀는데 안 늘어난다"로 보인다.
   *
   * ⚠ 이 값은 **접속시각과 다른 축이다**. 헤더 괄호의 접속시각은 `sessionStartedAt`이고,
   * 여기서 역산하지 않는다 — 유휴 기준이 되면서 이 값이 조작마다 뒤로 밀리기 때문에
   * 역산하면 접속시각이 조작할 때마다 앞으로 뛴다.
   */
  readonly sessionExpiresAt = signal<Date | null>(null);

  /**
   * 세션 유지시간(ms) — 서버 `REFRESH_TTL`을 응답으로 받은 값. 모르면 null.
   *
   * 이 값이 있으면 만료 시각을 **브라우저 자기 시계 + TTL**로 잡는다. 서버가 준 절대 시각을
   * 그대로 쓰면 왕복지연과 브라우저 시계 오차만큼 잔여시간이 깎여 보인다
   * ([연장] 직후 `119:55` — 대표 지적 2026-09-09).
   * 없으면(구 서버) 절대 시각으로 되돌아간다 — 지금까지의 동작 그대로다.
   */
  private readonly sessionTtlMsSignal = signal<number | null>(null);
  readonly sessionTtlMs = this.sessionTtlMsSignal.asReadonly();

  /**
   * 세션 만료로 강제 이탈 중인가 — `unsavedChangesGuard`가 이 값을 보고 확인창을 건너뛴다.
   * 만료는 사용자가 취소할 수 있는 동작이 아니다. 취소가 먹히면 토큰만 사라진 화면이 남아
   * 저장도 안 되고 이유도 알 수 없는 상태가 된다 (리뷰 C3).
   */
  private readonly sessionExpiredSignal = signal(false);
  readonly sessionExpired = this.sessionExpiredSignal.asReadonly();

  /**
   * 로그아웃 이동이 진행 중인가 — `guestGuard`가 이 값을 보고 통과시킨다.
   * 미저장 확인창을 먼저 띄우려고 세션 정리를 이동 **뒤로** 미루기 때문에, 그 사이에는
   * 토큰이 살아 있어 `guestGuard`가 /login을 /main으로 되돌린다(실측 9/8: 만료됐는데 메인으로
   * 돌아왔다). 이동 중이라는 사실을 가드에 알려 그 반송을 막는다.
   */
  private readonly loggingOutSignal = signal(false);
  readonly loggingOut = this.loggingOutSignal.asReadonly();

  /**
   * 세션 세대 — 로그인·세션 정리마다 1씩 오른다. "로그아웃 처리 중에 사용자가 다시
   * 로그인했는가"의 판정 근거다.
   *
   * 토큰 **값**으로 비교하면 안 된다: refresh 토큰은 인터셉터 회전으로 로그인 없이도 바뀌어,
   * 같은 세션인데 재로그인으로 오판한다 (재리뷰 C2). 세대는 회전에 오르지 않는다.
   */
  private sessionGeneration = 0;

  /** menuCode → 허용 동작 집합. hasPermission이 매 호출마다 배열을 훑지 않게 한 번만 만든다 */
  private readonly permissionIndex = computed(() => {
    const index = new Map<string, ReadonlySet<PermissionAction>>();
    for (const entry of this.permissions()) {
      index.set(entry.menuCode, new Set(entry.actions));
    }
    return index;
  });

  /**
   * 로그인 사용자의 역할 코드 — 로그인·`/auth/me`·메뉴 재조회가 모두 `user`를 갈아끼우므로
   * 여기서 파생하면 새로고침(restore) 뒤에도 같은 값이 나온다. 역할 미부여는 null.
   */
  readonly roleCode = computed<string | null>(() => this.user()?.roleCode ?? null);

  /**
   * 관리자 역할인가 — 계정 등록·권한 매트릭스 편집의 **역할 축** 판정.
   * 계층(orgType)·유효 권한과는 다른 축이라 셋을 함께 봐야 한다 (클라이언트 확정 2026-09-07:
   * 매트릭스 편집은 관리자 역할만). 서버도 같은 기준으로 셀 `editable`을 계산한다.
   */
  readonly isAdminRole = computed(() => isAdminRole(this.roleCode()));

  readonly accessToken = computed(() => this.accessTokenSignal());
  readonly refreshToken = computed(() => this.refreshTokenSignal());
  readonly isAuthenticated = computed(
    () => this.accessTokenSignal() !== null && this.user() !== null,
  );

  /**
   * 이 메뉴에서 이 동작이 허용되는가 — 서버 판정과 같은 값.
   * 목록에 없는 menuCode는 권한 없음(fail-closed)이다.
   */
  hasPermission(menuCode: string, action: PermissionAction): boolean {
    return this.permissionIndex().get(menuCode)?.has(action) ?? false;
  }

  login(request: LoginRequest): Observable<LoginResponse> {
    // 요청을 보낸 자기 시계 시각을 잡아둔다 — 만료 시각을 여기에 TTL을 얹어 잡기 때문이다
    const sentAt = Date.now();
    return this.http.post<LoginResponse>(`${this.apiBase}/auth/login`, request).pipe(
      tap((res) => {
        // 새 세션 — 지난 이탈의 가드 우회 두 축을 같은 자리에서 되돌리고 세대를 올린다.
        // (loggingOut을 빠뜨리면 진행 중이던 로그아웃의 finalize가 못 풀고 갇힌다 — 재리뷰 C1)
        this.sessionGeneration += 1;
        this.sessionExpiredSignal.set(false);
        this.loggingOutSignal.set(false);
        this.setTokens(res.accessToken, res.refreshToken);
        this.user.set(res.user);
        this.permissions.set(res.user.permissions);
        this.menus.set(res.menus);
        this.setSessionStartedAt(new Date());
        this.setSessionExpiry(res, sentAt);
      }),
    );
  }

  /**
   * 본인 비밀번호 변경 (JWT sub 기준) — 성공 204. 현재 비밀번호 불일치는 401인데,
   * 인터셉터가 refresh 1회 후 재시도하고 다시 401이면 그대로 전파하므로 호출부는 서버 메시지를 받는다.
   * 성공하면 서버가 **다른 세션의 refresh 토큰만** 폐기하고 현재 세션은 유지한다(2026-09-15,
   * JWT `sid`) — 재로그인 유도가 필요 없다.
   */
  changePassword(request: ChangePasswordRequest): Observable<void> {
    return this.http.post<void>(`${this.apiBase}/auth/change-password`, request);
  }

  /**
   * 비밀번호 찾기(1안, 대표 2026-09-07 결정) — 로그인 ID와 등록 이메일이 맞으면 서버가 임시
   * 비밀번호를 메일로 보낸다. 공개 엔드포인트라 인터셉터가 토큰을 붙이지 않는다(`BYPASS_URLS`).
   * 존재 여부를 드러내지 않으므로 맞든 틀리든 **같은 200·같은 문구**가 온다 — 화면은 그 문구를
   * 그대로 보여주고 성공·실패를 가르지 않는다.
   */
  findPassword(request: FindPasswordRequest): Observable<FindPasswordResponse> {
    return this.http.post<FindPasswordResponse>(`${this.apiBase}/auth/find-password`, request);
  }

  /**
   * 임시 비밀번호 상태 해제 — 강제 변경 다이얼로그가 성공으로 닫힌 직후 부른다.
   * 서버는 변경 시점에 플래그를 내렸지만 메모리의 `user`는 로그인 응답 그대로라, 여기서 내리지
   * 않으면 `authGuard`가 다음 이동에서 다시 다이얼로그를 띄운다.
   */
  clearMustChangePassword(): void {
    this.user.update((user) =>
      user === null || !user.mustChangePassword ? user : { ...user, mustChangePassword: false },
    );
  }

  /** 인터셉터 전용 — refresh rotation 호출 (토큰 반영은 호출부 책임) */
  requestRefresh(refreshToken: string): Observable<RefreshResponse> {
    return this.http.post<RefreshResponse>(`${this.apiBase}/auth/refresh`, { refreshToken });
  }

  /**
   * 세션 연장 — 만료 시각을 호출 시점 기준으로 다시 잡는다.
   *
   * 유휴 만료가 되면서 `/auth/refresh`도 회전할 때마다 같은 일을 한다. 그래도 이 통로가
   * 따로 있는 이유는 두 가지다 — 대표가 버튼을 명시적으로 요구했고(2026-09-08), 서버가
   * 이 경로만 `SESSION_EXTEND`로 감사에 남긴다(자동 회전은 남기지 않는다).
   * 응답 계약은 refresh와 같아 반영 절차도 같다: 토큰 저장 + 만료 시각 + 권한.
   * 반영은 호출부(SessionCountdownService) 책임이다 — 응답을 기다리는 사이 세션이
   * 바뀌었는지 판정한 뒤에 써야 하기 때문이다.
   */
  requestExtend(refreshToken: string): Observable<RefreshResponse> {
    return this.http.post<RefreshResponse>(`${this.apiBase}/auth/extend`, { refreshToken });
  }

  /**
   * 지금 세션의 세대 값 — 비동기 작업 전후로 비교해 "그 사이 세션이 바뀌었는가"를 판정한다.
   * refresh 토큰 **값**으로 비교하면 안 된다: 인터셉터 회전·연장이 로그인 없이도 값을 바꾼다.
   */
  currentSessionGeneration(): number {
    return this.sessionGeneration;
  }

  /**
   * 로그아웃 — 서버 revoke 성공 여부와 무관하게 로컬 세션을 정리하고 /login으로 이동.
   * (네트워크 실패로 로그아웃이 막히면 안 된다 — fc4df17c 결정 6)
   *
   * **이동이 먼저, 세션 정리가 나중이다.** 미저장 폼에서는 `unsavedChangesGuard`가 확인창을
   * 띄우는데, 예전처럼 토큰부터 지우면 사용자가 "취소"를 눌렀을 때 화면은 그대로인데 세션만
   * 사라져 이후 모든 요청이 401이 된다 (리뷰 C3). 취소하면 로그아웃 자체가 없던 일이 된다.
   */
  logout(reason?: 'expired'): void {
    if (reason === 'expired') {
      // 만료는 사용자가 취소할 수 있는 동작이 아니다 — 가드가 확인창 없이 통과시킨다
      this.sessionExpiredSignal.set(true);
    }
    this.loggingOutSignal.set(true);
    // **세션 동일성은 세대로 판정한다.** refresh 토큰 값으로 비교하면 인터셉터의 회전
    // (로그인 없이도 일어난다)을 재로그인으로 오판해 clearSession을 건너뛴다 —
    // 화면은 로그인 페이지인데 토큰이 살아 있어 뒤로가기 한 번에 되들어간다 (재리뷰 C2)
    const generation = this.sessionGeneration;
    void this.goToLogin(reason).then((left) => {
      if (!left && reason === undefined) {
        this.loggingOutSignal.set(false);
        return; // 사용자가 이탈을 취소했다 — 세션을 유지한다 (로그아웃 취소)
      }
      if (this.sessionGeneration !== generation) {
        // 이동하는 사이 진짜로 재로그인했다 — 옛 세션은 그 로그인이 이미 갈아치웠다.
        // 새 세션의 토큰을 폐기하거나 정리하면 방금 로그인한 사용자를 다시 끊는다
        this.loggingOutSignal.set(false);
        if (!left) {
          window.location.assign('/login?reason=expired');
        }
        return;
      }
      const finalize = () => {
        // 그 사이 **진짜로 재로그인했으면**(세대 증가) 새 세션을 건드리지 않는다.
        // 그 경우에도 이 신호는 반드시 풀어야 한다 — 갇히면 guestGuard가 영구 통과라
        // 로그인한 사용자에게 /login 폼이 그대로 뜬다 (재리뷰 C1)
        if (this.sessionGeneration === generation) {
          this.clearSession();
        } else {
          this.loggingOutSignal.set(false);
        }
        if (!left) {
          // 만료인데 라우팅이 막혔다 — revoke가 끝난 뒤 페이지를 통째로 교체한다.
          // (먼저 교체하면 진행 중인 revoke가 중단돼 서버에 활성 refresh 행이 남는다)
          window.location.assign('/login?reason=expired');
        }
      };
      // 전송 직전에 다시 읽는다 — 이동하는 사이 인터셉터가 회전했으면 폐기해야 할 것은
      // 그 **새 토큰**이다. 옛 값을 보내면 서버가 거부하고 회전된 행이 활성으로 남는다
      const refreshToken = this.refreshTokenSignal();
      if (refreshToken === null) {
        finalize();
        return;
      }
      // revoke는 clearSession보다 먼저다 — 토큰을 지운 뒤에는 /auth/logout이 401로 튕겨
      // 서버에 활성 refresh 행이 남는다. 영구 누적은 아니고 다음 로그인 때 서버가 상한을 넘긴
      // 오래된 행부터 지우지만(pruneOwnerTokens), 그 자리를 차지한 만큼 **다른 기기의 세션이
      // 먼저 밀려난다**.
      // 다만 응답을 무한정 기다리지는 않는다: HttpClient에는 기본 타임아웃이 없어 서버가
      // 묵묵부답이면 finalize가 아예 돌지 않고, 공용 PC에 화면만 로그인 페이지인 채 살아 있는
      // 세션이 남는다. 서버 행은 절대 만료로 회수되지만 로컬 세션이 남는 쪽이 훨씬 나쁘다
      // ("네트워크가 죽어도 로그아웃은 된다" — fc4df17c 결정 6 / 재리뷰 C3)
      this.http
        .post(`${this.apiBase}/auth/logout`, { refreshToken })
        .pipe(timeout(REVOKE_TIMEOUT_MS))
        .subscribe({ next: finalize, error: finalize });
    })
    .catch((err: unknown) => {
      // finalize·clearSession이 던질 수 있다(스토리지를 막은 브라우저). 미처리로 두면
      // loggingOut이 true로 갇혀 guestGuard가 영구 통과한다 — C1에서 막은 상태가
      // 다른 경로로 재현된다. 신호부터 풀고 근거를 남긴다
      this.loggingOutSignal.set(false);
      console.warn('[auth] 로그아웃 마무리 중 오류 — 로컬 세션을 정리합니다.', err);
      try {
        this.clearSession();
      } catch {
        // 저장소 접근 자체가 막힌 경우다. 메모리 상태만이라도 비운다
        this.accessTokenSignal.set(null);
        this.refreshTokenSignal.set(null);
        this.user.set(null);
      }
    });
  }

  /**
   * 세션이 서버에서 이미 끊겼다 — refresh 실패처럼 **폐기할 것이 남아 있지 않은** 경로 전용
   * (인터셉터). 로그인 화면에 자동 로그아웃 안내가 뜨도록 사유를 붙여 내보낸다.
   */
  expireSession(reason: LogoutReason = 'expired'): void {
    // 재진입 가드 — 뒤늦게 응답한 요청의 401이 새 refreshOnce를 만들어 여기로 다시 온다.
    // 이미 /login이면 라우터가 같은 URL 이동을 건너뛰어 falsy를 주고, 그게 하드 리다이렉트로
    // 이어져 **입력 중이던 아이디·비밀번호가 날아간다** (재리뷰 W3)
    if (this.sessionExpiredSignal()) {
      return;
    }
    this.sessionExpiredSignal.set(true);
    this.clearSession();
    void this.goToLogin(reason)
      .then((left) => {
        if (!left) {
          // 폐기할 것이 없어 기다릴 요청도 없다 — 바로 페이지를 교체해 화면을 확실히 끊는다
          window.location.assign(`/login?reason=${reason}`);
        }
      })
      .catch((err: unknown) => {
        // 여기서 삼키지 않으면 미처리 rejection이 된다. 세션은 이미 정리된 뒤라 남은 일은 없다
        console.warn('[auth] 세션 만료 이동 중 오류', err);
      });
  }

  /**
   * /login 이동. 만료 이탈은 무슨 일이 있어도 성사돼야 하므로 실패(false)를 호출부에 알린다 —
   * 토큰 없는 화면이 남으면 사용자는 저장도 안 되고 왜 안 되는지도 모른다 (리뷰 C3).
   */
  private async goToLogin(reason?: LogoutReason): Promise<boolean> {
    // 이미 로그인 화면이면 갈 곳이 없다 — 라우터는 같은 URL 이동을 건너뛰어 falsy를 주고, 평소
    // 로그아웃은 그것을 "취소"로 읽어 세션을 남긴다. 임시 비밀번호 강제 변경 창은 로그인 직후
    // `/login` 위에 뜨므로(가드가 /main 이동을 취소한다) 거기서 [로그아웃]을 누르면 이 경우다.
    // 사유가 있는 이탈은 그대로 둔다 — 하드 리다이렉트로 사유 문구를 다시 그려야 한다
    if (reason === undefined && this.router.url.startsWith('/login')) {
      return true;
    }
    const extras = reason === undefined ? {} : { queryParams: { reason } };
    let rejectedByGuard = false;
    const watch = this.router.events.subscribe((event) => {
      if (event instanceof NavigationCancel) {
        rejectedByGuard = event.code === NavigationCancellationCode.GuardRejected;
      }
    });
    try {
      const left = await this.router.navigate(['/login'], extras).catch(() => false);
      if (left || rejectedByGuard) {
        return left;
      }
      // 가드가 막은 게 아니라 다른 이동에 밀렸거나 라우팅이 실패한 경우다. 이걸 "취소"로
      // 읽으면 로그아웃을 눌렀는데 아무 일도 일어나지 않은 것처럼 보인다 — 한 번 더 시도한다
      // (가드 거부는 여기 오지 않으므로 확인창이 두 번 뜨지 않는다)
      return await this.router.navigate(['/login'], extras).catch(() => false);
    } finally {
      watch.unsubscribe();
    }
  }

  /**
   * 앱 부팅 시 세션 복원 (provideAppInitializer).
   * 저장 토큰이 있으면 /auth/me + /menus로 유효성을 검증한다.
   * access 만료 시 인터셉터가 refresh 1회를 수행하므로 여기선 결과만 본다.
   */
  async restore(): Promise<void> {
    this.syncFromStorage();
    if (this.accessTokenSignal() === null || this.refreshTokenSignal() === null) {
      this.clearSession();
      return;
    }
    try {
      const [me, menus] = await firstValueFrom(
        forkJoin([
          this.http.get<MeResponse>(`${this.apiBase}/auth/me`),
          this.http.get<MenuItem[]>(`${this.apiBase}/menus`),
        ]),
      );
      this.user.set(me);
      this.permissions.set(me.permissions);
      this.menus.set(menus);
      // 저장된 최초 로그인 시각을 되살린다 — 여기서 지금 시각을 넣으면 F5 한 번에 헤더
      // 접속시각이 방금으로 뛴다(대표가 "접속시간은 계속 그대로"라고 지정한 값이다).
      // 저장값이 없거나 깨졌으면(옛 세션·수동 삭제) 그때만 지금으로 채운다 — 접속시각이
      // 통째로 사라지면 헤더의 잔여시간까지 함께 감춰진다(템플릿이 이 값으로 묶여 있다)
      this.setSessionStartedAt(
        parseExpiresAt(localStorage.getItem(STORAGE_SESSION_STARTED)) ?? new Date(),
      );
      // 새로고침 복원이 만료 시각을 받는 유일한 통로. 옛 토큰이면 값이 없어 null이 되고
      // (저장돼 있던 값도 함께 지워진다) 카운트다운은 표시되지 않는다
      // 복원에는 anchor를 주지 않는다 — 새로고침은 조작이 아니라서 TTL을 얹으면 F5만으로
      // 세션이 늘어난다. 서버가 아는 절대 만료 시각을 그대로 쓴다
      this.setSessionExpiry(me);
    } catch (err) {
      // 인증 거부(401/403)일 때만 세션을 파기한다. 서버 순단·5xx·네트워크 오류(status 0)에서
      // 토큰까지 지우면 서버 복구 후에도 재로그인이 필요해진다 — 토큰은 보존한 채 게스트로
      // 시작하고, 이후 요청에서 인터셉터가 자연 복구한다 (인터셉터의 401/403 구분과 동일 기준)
      const status = err instanceof HttpErrorResponse ? err.status : 0;
      if (status === 401 || status === 403) {
        this.clearSession();
      } else {
        this.user.set(null);
        this.permissions.set([]);
        this.menus.set([]);
        this.sessionStartedAt.set(null);
        this.sessionExpiresAt.set(null);
        // TTL도 함께 버린다 — 만료 시각을 모르는 채 TTL만 남으면 다음 조작에서 근거 없이
        // `지금 + 120분`이 그려진다
        this.sessionTtlMsSignal.set(null);
      }
    }
  }

  /**
   * 메뉴만 재조회한다 — PAGE관리 저장으로 자기 계층의 노출 집합이 바뀌었을 때
   * 셸 사이드바를 즉시 반영하기 위한 통로.
   *
   * 실패해도 세션·기존 메뉴는 **건드리지 않는다**: 메뉴 갱신은 부가 동기화라
   * 여기서 로그아웃시키거나 사이드바를 비우면 저장에 성공한 사용자가 화면을 잃는다.
   * 호출부가 실패를 안내할 수 있도록 예외는 삼키고 boolean으로 알린다.
   */
  async refreshMenus(): Promise<boolean> {
    try {
      // 권한도 함께 읽는다 — 노출(menu_visibility)과 권한(user_menu_permission)은 다른 축이지만
      // 둘 다 화면 구성의 근거라, 한쪽만 갱신하면 사이드바와 버튼이 서로 다른 시점을 가리킨다
      const [me, menus] = await firstValueFrom(
        forkJoin([
          this.http.get<MeResponse>(`${this.apiBase}/auth/me`),
          this.http.get<MenuItem[]>(`${this.apiBase}/menus`),
        ]),
      );
      this.user.set(me);
      this.permissions.set(me.permissions);
      this.menus.set(menus);
      return true;
    } catch (err: unknown) {
      // 세션·메뉴는 그대로 두되, 왜 사이드바가 갱신되지 않았는지는 콘솔에 남긴다
      console.warn('[auth] 메뉴·권한 재조회 실패 — 기존 값을 유지합니다.', err);
      return false;
    }
  }

  setTokens(accessToken: string, refreshToken: string): void {
    this.accessTokenSignal.set(accessToken);
    this.refreshTokenSignal.set(refreshToken);
    localStorage.setItem(STORAGE_ACCESS, accessToken);
    localStorage.setItem(STORAGE_REFRESH, refreshToken);
  }

  /**
   * 세션 만료 시각 반영 — 로그인·복원(`/auth/me`)·회전·연장 **네 경로가 모두** 부른다.
   * 하나라도 빠지면 그 경로를 탄 뒤 헤더 카운트다운이 사라지거나 옛 값에 머문다.
   *
   * `anchorMs`는 **그 요청을 보낸 브라우저 시각**이다. 서버가 TTL을 함께 내렸으면 만료 시각을
   * `anchorMs + TTL`로 잡는다 — 서버가 준 절대 시각을 그대로 쓰면 왕복지연·시계 오차가
   * 잔여시간에서 깎여 보이기 때문이다([연장] 직후 `119:55`, 대표 지적 2026-09-09).
   * 서버 판정보다 최대 (왕복지연 + 시계 오차)만큼 늦게 끊기지만, 그 구간의 요청은 서버가
   * 401로 자르고 인터셉터가 세션을 정리하므로 화면이 죽은 세션에 남지 않는다.
   *
   * `anchorMs`가 없거나(복원) TTL을 모르면(구 서버) 서버가 준 절대 시각을 그대로 쓴다.
   * 만료 시각이 없으면 저장된 값도 함께 지운다 — 서버가 모르는 만료 시각을 화면이 우기지 않게.
   */
  setSessionExpiry(
    res: { sessionExpiresAt?: string; sessionTtlSeconds?: number },
    anchorMs?: number,
  ): void {
    const ttlMs = parseTtlMs(res.sessionTtlSeconds);
    this.sessionTtlMsSignal.set(ttlMs);
    if (ttlMs === null) {
      localStorage.removeItem(STORAGE_SESSION_TTL);
    } else {
      localStorage.setItem(STORAGE_SESSION_TTL, String(ttlMs / 1000));
    }
    const parsed =
      ttlMs !== null && anchorMs !== undefined
        ? new Date(anchorMs + ttlMs)
        : parseExpiresAt(res.sessionExpiresAt);
    this.writeSessionExpiresAt(parsed);
  }

  /**
   * 조작 직후의 낙관적 반영 — 화면 잔여시간을 `activityAt + TTL`로 되돌린다.
   *
   * 서버 갱신은 스로틀(60초)이 걸려 있어 매 조작마다 나가지 않는다. 화면까지 서버 응답을
   * 기다리면 "눌렀는데 시간이 안 늘어난다"로 보이므로 표시만 먼저 민다. 스로틀의 트레일링
   * 호출이 뒤따라 서버를 같은 자리로 끌어오기 때문에 어긋남은 스로틀 간격 안에서 해소된다.
   *
   * **뒤로는 절대 밀지 않는다**(`<=` 이면 무시) — 서버가 이미 더 늦은 만료를 확정했는데
   * 이 값으로 덮으면 세션을 스스로 줄이게 된다.
   * TTL을 모르면 아무것도 하지 않는다 — 화면이 근거 없는 시간을 우기지 않게.
   */
  pushSessionExpiryFromActivity(activityAtMs: number): void {
    const ttlMs = this.sessionTtlMsSignal();
    if (ttlMs === null) {
      return;
    }
    const next = new Date(activityAtMs + ttlMs);
    const current = this.sessionExpiresAt();
    if (current !== null && next.getTime() <= current.getTime()) {
      return;
    }
    this.writeSessionExpiresAt(next);
  }

  /**
   * 최초 로그인 시각 반영 — 로그인에서 정하고 복원에서 되살린다.
   * 토큰과 같은 방식으로 저장하는 이유도 같다: 새로고침을 건너뛰고 살아남아야 한다.
   */
  private setSessionStartedAt(value: Date): void {
    this.sessionStartedAt.set(value);
    localStorage.setItem(STORAGE_SESSION_STARTED, value.toISOString());
  }

  private writeSessionExpiresAt(value: Date | null): void {
    this.sessionExpiresAt.set(value);
    if (value === null) {
      localStorage.removeItem(STORAGE_SESSION_EXPIRES);
    } else {
      localStorage.setItem(STORAGE_SESSION_EXPIRES, value.toISOString());
    }
  }

  /**
   * 만료 시각만 storage에서 재독한다 (카운트다운 tick 전용 멀티탭 방어).
   *
   * 탭 B를 열어둔 채 탭 A에서 재로그인하면 탭 B의 시그널은 **메모리에 남은 옛 값**이다.
   * 백그라운드 탭 타이머는 분당 1회로 스로틀링돼 판정이 늦게 도착하는데, 그때 탭 B가
   * 옛 값으로 만료를 선언하면 localStorage를 지워 **탭 A의 새 세션까지 끊는다** (리뷰 W2).
   *
   * 값이 같으면 시그널을 건드리지 않는다 — 매 초 새 Date를 넣으면 파생 계산이 헛돈다.
   */
  syncSessionExpiryFromStorage(): void {
    const stored = parseExpiresAt(localStorage.getItem(STORAGE_SESSION_EXPIRES));
    if (stored?.getTime() !== this.sessionExpiresAt()?.getTime()) {
      this.sessionExpiresAt.set(stored);
    }
    // TTL도 같은 이유로 재독한다 — 다른 탭이 재로그인했으면 이 탭의 TTL이 옛 세션의 값이고,
    // 그걸로 조작 시 만료를 밀면 새 세션의 만료 시각이 틀어진다
    const ttlMs = parseTtlMs(localStorage.getItem(STORAGE_SESSION_TTL));
    if (ttlMs !== this.sessionTtlMsSignal()) {
      this.sessionTtlMsSignal.set(ttlMs);
    }
  }

  /**
   * 다른 탭이 세션을 끝냈는가 — storage에는 refresh 토큰이 없는데 이 탭은 아직 들고 있다.
   * 카운트다운 tick이 매 초 확인해, 죽은 세션으로 화면만 남는 상태를 끊는다 (재리뷰 W4).
   */
  isSessionEndedElsewhere(): boolean {
    return this.refreshTokenSignal() !== null && localStorage.getItem(STORAGE_REFRESH) === null;
  }

  /** 다른 탭이 먼저 rotation·로그아웃했을 수 있어 storage를 재독한다 (인터셉터 멀티탭 방어) */
  syncFromStorage(): void {
    this.accessTokenSignal.set(localStorage.getItem(STORAGE_ACCESS));
    this.refreshTokenSignal.set(localStorage.getItem(STORAGE_REFRESH));
    this.sessionExpiresAt.set(parseExpiresAt(localStorage.getItem(STORAGE_SESSION_EXPIRES)));
    this.sessionTtlMsSignal.set(parseTtlMs(localStorage.getItem(STORAGE_SESSION_TTL)));
  }

  /** refresh rotation 응답의 권한 반영 — 세션이 오래 살아도 회수가 access 수명 안에 반영된다 */
  applyRefreshedPermissions(permissions: EffectivePermissions): void {
    this.permissions.set(permissions);
    this.user.update((user) => (user === null ? null : { ...user, permissions }));
  }

  clearSession(): void {
    this.sessionGeneration += 1;
    this.accessTokenSignal.set(null);
    this.refreshTokenSignal.set(null);
    this.user.set(null);
    this.permissions.set([]);
    this.menus.set([]);
    this.sessionStartedAt.set(null);
    this.sessionExpiresAt.set(null);
    this.sessionTtlMsSignal.set(null);
    this.loggingOutSignal.set(false); // 이동이 끝났다 — 다음 /login 진입은 평소 규칙대로
    localStorage.removeItem(STORAGE_ACCESS);
    localStorage.removeItem(STORAGE_REFRESH);
    localStorage.removeItem(STORAGE_SESSION_EXPIRES);
    localStorage.removeItem(STORAGE_SESSION_TTL);
    localStorage.removeItem(STORAGE_SESSION_STARTED);
  }
}
